import { DISCIPLINES } from '@batiflow/contracts';
import { DisciplineTag, SkeletonList, isolate } from '@batiflow/ui';
import {
  Button,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  __css,
  tokens,
} from '@fluentui/react-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { useDirectorySearch } from './api';

const useStyles = __css({"root":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fgdu4nw","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f778t4r","mlrwpw":"fs2zvw3","Bmxbyg5":"f5zp4f"},"row":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","Brf1p80":"f1869bpl","i8kkvl":0,"Belr9w4":0,"rmohyg":"fu9asb0","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f47sw1k","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f5j02bv","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","De3pzq":"f1dmdbja"},"names":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9"},"chips":{"mc9l5x":"f22iagw","Eh141a":"fni485r","i8kkvl":0,"Belr9w4":0,"rmohyg":"fkln5zr"},"label":{"Be2twd7":"fkhj508","Bhrd7zp":"fl43uef"}});

export interface DirectoryChoice {
  organizationId: string;
  name: string;
}

export interface DirectoryPickerProps {
  onPick: (choice: DirectoryChoice) => void;
  onCancel: () => void;
}

/**
 * Search of the partner directory used inside the invitation dialog: a name box, then the offices
 * with a "Choisir" button each. It must not submit the surrounding form (Enter does nothing).
 */
export function DirectoryPicker({ onPick, onCancel }: DirectoryPickerProps) {
  const { t } = useTranslation('spaces');
  const { t: tCommon } = useTranslation('common');
  const styles = useStyles();
  const [search, setSearch] = useState('');
  const q = useDebouncedValue(search.trim(), 300);
  const result = useDirectorySearch(q ? { q } : {});
  const entries = result.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className={styles.root}>
      <label className={styles.names}>
        <span className={styles.label}>{t('sharing.share.directory.searchLabel')}</span>
        <Input
          type="search"
          value={search}
          maxLength={100}
          onChange={(_event, data) => {
            setSearch(data.value);
          }}
          // Enter searches as you type; it must not submit the share form around the picker.
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.preventDefault();
          }}
        />
      </label>
      {result.isError ? (
        <MessageBar intent="error" layout="multiline">
          <MessageBarBody>{t('sharing.share.directory.loadFailed')}</MessageBarBody>
        </MessageBar>
      ) : result.isPending ? (
        <SkeletonList count={2} />
      ) : entries.length === 0 ? (
        <p>{t('sharing.share.directory.empty')}</p>
      ) : (
        <ul className={styles.list} aria-label={t('sharing.share.directory.results')}>
          {entries.map((entry) => (
            <li key={entry.organizationId} className={styles.row}>
              <span className={styles.names}>
                <bdi>{entry.name}</bdi>
                <span className={styles.chips}>
                  {DISCIPLINES.filter((code) => entry.disciplines.includes(code)).map((code) => (
                    <DisciplineTag key={code} discipline={code} />
                  ))}
                </span>
              </span>
              <Button
                size="small"
                appearance="primary"
                aria-label={t('sharing.share.directory.chooseLabel', { name: isolate(entry.name) })}
                onClick={() => {
                  onPick({ organizationId: entry.organizationId, name: entry.name });
                }}
              >
                {t('sharing.share.directory.choose')}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {result.hasNextPage ? (
        result.isFetchingNextPage ? (
          <Spinner size="tiny" />
        ) : (
          <Button
            size="small"
            appearance="secondary"
            onClick={() => {
              void result.fetchNextPage();
            }}
          >
            {t('sharing.share.directory.more')}
          </Button>
        )
      ) : null}
      <div>
        <Button type="button" size="small" appearance="subtle" onClick={onCancel}>
          {tCommon('actions.cancel')}
        </Button>
      </div>
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/directory/directory-picker.griffel.css";